<template>
  <div class="d-flex align-center justify-center flex-column text-center">
    <div class="d-flex justify-center align-center mb-4">
      <img src="/favicon.svg" width="48" height="48" class="mr-2" alt="Continuum Design" />
      <v-icon size="48">mdi-swap-horizontal</v-icon>
      <v-icon size="48" class="ml-2">mdi-github</v-icon>
    </div>
    <v-select
      data-cy="select-github-account"
      :model-value="selectedAccount"
      @update:model-value="handleAccountSelection"
      :items="githubAccounts"
      label="Select GitHub Account"
      item-title="login"
      item-value="login"
      variant="outlined"
      density="comfortable"
      style="width: 420px"
      return-object
    >
      <template #selection="{ item }">
        <template v-if="item && !item.raw.isAddNew">
          <v-avatar size="24" class="mr-2">
            <img :src="item.raw.avatar" :alt="item.raw.login" class="w-100 h-100" />
          </v-avatar>
          <span>{{ item.raw.login }}</span>
        </template>
        <template v-else>
          <v-icon class="mr-2">mdi-plus</v-icon>
          <span>Add another account</span>
        </template>
      </template>
      <template #item="{ item, props }">
        <template v-if="!item.raw.isAddNew">
          <div v-bind="props" class="d-flex align-center px-2 py-3 cursor-pointer">
            <v-avatar size="24" class="mr-2">
              <img :src="item.raw.avatar" :alt="item.raw.login" class="w-100 h-100" />
            </v-avatar>
            <span>{{ item.raw.login }}</span>
          </div>
        </template>
        <template v-else>
          <div v-bind="props" class="d-flex align-center px-2 py-3 cursor-pointer">
            <v-icon class="mr-2">mdi-plus</v-icon>
            <span>Add another account</span>
          </div>
        </template>
      </template>
    </v-select>
    <v-list class="border" style="width: 420px;height: 240px;">
      <div v-if="isLoadingRepos && repositories.length === 0" class="d-flex justify-center align-center py-8">
        <div class="text-center">
          <v-progress-circular
            indeterminate
            size="48"
            color="primary"
          />
          <p class="text-caption mt-4 text-medium-emphasis">Fetching repositories...</p>
        </div>
      </div>
      <template v-else>
        <v-list-item
          v-for="repo in repositories"
          :key="repo.full_name"
          :active="selectedRepo && selectedRepo.full_name === repo.full_name"
          @click="handleRepoSelection(repo)"
          class="cursor-pointer"
        >
          <template v-slot:prepend>
            <v-icon icon="mdi-source-repository"></v-icon>
          </template>
          <v-list-item-title>{{ repo.name }}</v-list-item-title>
          <v-list-item-subtitle v-if="repo.description">
            {{ repo.description }}
          </v-list-item-subtitle>
          <template v-slot:append>
            <v-icon :icon="repo.private ? 'mdi-lock-outline' : 'mdi-lock-open-variant-outline'" color="grey" />
          </template>
        </v-list-item>

        <v-list-item v-if="nextReposPageUrl" ref="repoSentinel">
          <div class="d-flex justify-center align-center py-2">
            <v-progress-circular indeterminate size="24" color="primary" />
          </div>
        </v-list-item>
      </template>
    </v-list>

    <v-autocomplete
      data-cy="select-repo-branch"
      :model-value="selectedBranch"
      @update:model-value="handleBranchSelection"
      :items="branches"
      item-title="name"
      item-value="name"
      label="Select or search for a Branch"
      variant="outlined"
      density="comfortable"
      style="width: 420px"
      class="mt-4"
      :loading="isLoadingBranches"
      :disabled="!selectedRepo"
      prepend-inner-icon="mdi-source-branch"
      hide-details
    >
      <template #item="{ item, props }">
        <v-list-item v-bind="props" :title="item.raw.name">
          <template #prepend>
            <v-icon v-if="item.raw.protected" title="Protected Branch">mdi-lock-check-outline</v-icon>
          </template>
        </v-list-item>
      </template>

      <template #append-item>
        <div v-if="nextBranchesPageUrl" ref="branchSentinel" class="d-flex justify-center pa-2">
          <v-progress-circular v-if="isLoadingBranches" indeterminate size="24" color="primary" />
        </div>
      </template>
    </v-autocomplete>

    <v-btn-group v-if="!hideButtons" class="mt-4" variant="outlined" block style="width: 420px;">
      <v-btn
        class="w-50" style="border-radius: 0 !important;"
        :disabled="!selectedAccount || store.state.isWorkflowDisabled"
        @click="isCreateDialogOpen = true"
      >
        <v-icon class="mr-2">mdi-plus</v-icon> Create Repository
      </v-btn>
      <v-divider vertical />
      <v-btn
        class="w-50" style="border-radius: 0 !important;"
        :disabled="!selectedAccount || !selectedRepo || store.state.isWorkflowDisabled"
        @click="emit('onLinkRepo')"
      >
        <v-icon class="mr-2">mdi-link</v-icon> Link Repository
      </v-btn>
    </v-btn-group>
    <create-repo-dialog
      v-model="isCreateDialogOpen"
      :githubAccountLogin="selectedAccount?.login"
      @onRepoCreated="(newRepo) => emit('onRepoCreated', newRepo)"
    />
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useStore } from 'vuex'
import gitHubApi from '@/api/gitHubApi'
import CreateRepoDialog from '@/components/CreateRepoDialog.vue'

const { selectedAccount, selectedRepo, selectedBranch, hideButtons } = defineProps({
  selectedAccount: Object,
  selectedRepo: Object,
  selectedBranch: String,
  hideButtons: Boolean
})
const emit = defineEmits([
  'update:selectedRepo',
  'update:selectedBranch',
  'update:selectedAccount',
  'onRepoCreated',
  'onLinkRepo'
])

const repositories = ref([])
const branches = ref([])
const isLoadingRepos = ref(false)
const isLoadingBranches = ref(false)
const isCreateDialogOpen = ref(false)
const currentOrigin = window.location.origin

const nextReposPageUrl = ref(null)
const repoSentinel = ref(null)
let observerRepos = null

const nextBranchesPageUrl = ref(null)
const branchSentinel = ref(null)
let observerBranches = null

const store = useStore()
const connectedGithubAccounts = computed(() => store.state.github.githubAccounts)
const githubAccounts = computed(() => {
  return [...connectedGithubAccounts.value, { isAddNew: true }]
})

const handleAccountSelection = async (account) => {
  if (account && account.isAddNew) {
    if (store.state.isWorkflowDisabled) return
    await connectGithub()
  } else {
    emit('update:selectedAccount', account)
    await listRepos(account.login)
  }
}

const handleRepoSelection = async (repo) => {
  emit('update:selectedRepo', repo)

  if (repo) {
    await listRepoBranches(selectedAccount.login, repo.owner.login, repo.name)
  } else {
    branches.value = []
    nextBranchesPageUrl.value = null
  }
}

const handleBranchSelection = (branchName) => {
  emit('update:selectedBranch', branchName)
}

const connectGithub = async () => {
  const clientId = process.env.VUE_APP_GITHUB_CLIENT_ID
  const redirectUri = encodeURIComponent(`${currentOrigin}/github/callback`)
  const scope = 'repo'
  const authUrl = `https://github.com/login/oauth/authorize?client_id=${clientId}&scope=${scope}&redirect_uri=${redirectUri}`

  window.open(authUrl, '_blank', 'width=600,height=700')
}

const githubAuthListener = async (event) => {
  if (event.origin !== currentOrigin) {
    return
  }

  if (event.data.type === 'github-auth') {
    const authCode = event.data.code

    const result = await gitHubApi.saveGitHubToken(authCode)
    if (!result?.success || !result?.resData?.githubAccounts?.length) {
      return
    }

    const accounts = result?.resData?.githubAccounts
    const { login } = accounts[accounts.length - 1]

    emit('update:selectedAccount', accounts[accounts.length - 1])
    await listRepos(login)
  }
}

const listRepos = async (login, pageUrl = null) => {
  if (isLoadingRepos.value) {
    return
  }
  isLoadingRepos.value = true

  if (!pageUrl) {
    repositories.value = []
    branches.value = []
    nextReposPageUrl.value = null
    nextBranchesPageUrl.value = null
    emit('update:selectedRepo', null)
    emit('update:selectedBranch', null)
  }

  try {
    const response = await gitHubApi.listGitHubRepos(login, pageUrl)

    if (response?.resData?.repos?.length > 0) {
      repositories.value.push(...response.resData.repos)
    }
    nextReposPageUrl.value = response.resData.nextPageUrl
  } catch (error) {
    store.commit('snackbar/showMessage', {
      content: `Error fetching repositories: ${error.message}`,
      color: 'error'
    }, { root: true })
  } finally {
    isLoadingRepos.value = false
  }
}

const listRepoBranches = async (login, owner, repo, pageUrl = null) => {
  if (isLoadingBranches.value) {
    return
  }
  isLoadingBranches.value = true

  if (!pageUrl) {
    branches.value = []
    nextBranchesPageUrl.value = null
    emit('update:selectedBranch', null)
  }

  try {
    const response = await gitHubApi.listRepoBranches(login, owner, repo, pageUrl)

    if (response?.success && response.resData?.branches) {
      branches.value.push(...response.resData.branches)
      nextBranchesPageUrl.value = response.resData.nextPageUrl

      if (!pageUrl) {
        const defaultBranch = branches.value.find((b) => b.name === 'main' || b.name === 'master') || branches.value[0]
        if (defaultBranch) {
          handleBranchSelection(defaultBranch.name)
        }
      }
    }
  } catch (error) {
    store.commit('snackbar/showMessage', {
      content: `Error fetching branches: ${error.message}`,
      color: 'error'
    }, { root: true })
  } finally {
    isLoadingBranches.value = false
  }
}

const setupReposObserver = () => {
  observerRepos = new IntersectionObserver((entries) => {
    const [entry] = entries
    if (entry.isIntersecting && !isLoadingRepos.value && nextReposPageUrl.value) {
      listRepos(selectedAccount.login, nextReposPageUrl.value)
    }
  })

  if (repoSentinel.value) {
    observerRepos.observe(repoSentinel.value.$el)
  }
}

const setupBranchesObserver = () => {
  observerBranches = new IntersectionObserver((entries) => {
    const [entry] = entries
    if (entry.isIntersecting && !isLoadingBranches.value && nextBranchesPageUrl.value) {
      const { login } = selectedAccount
      const { owner, name } = selectedRepo
      listRepoBranches(login, owner.login, name, nextBranchesPageUrl.value)
    }
  })

  if (branchSentinel.value) {
    observerBranches.observe(branchSentinel.value)
  }
}

watch(repoSentinel, (newValue, oldValue) => {
  if (oldValue) {
    observerRepos?.unobserve(oldValue.$el)
  }
  if (newValue) {
    setupReposObserver()
  }
})

watch(branchSentinel, (newValue, oldValue) => {
  if (oldValue) {
    observerBranches?.unobserve(oldValue)
  }
  if (newValue) {
    setupBranchesObserver()
  }
})

onMounted(async () => {
  window.addEventListener('message', githubAuthListener)

  if (selectedAccount && selectedAccount.login) {
    await listRepos(selectedAccount.login)
    return
  }

  if (!connectedGithubAccounts.value?.length || connectedGithubAccounts.value?.length > 1) {
    return
  }

  emit('update:selectedAccount', connectedGithubAccounts.value[0])
  const { login } = connectedGithubAccounts.value[0]

  if (login) {
    await listRepos(login)
  }
})

onBeforeUnmount(() => {
  window.removeEventListener('message', githubAuthListener)
  if (observerRepos) {
    observerRepos.disconnect()
  }
  if (observerBranches) {
    observerBranches.disconnect()
  }
})
</script>
